import React, { useEffect, useState, createElement } from 'react';
import { Button } from './ui/Button';
import { X, CheckCircleIcon } from 'lucide-react';
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    gtag: (...args: unknown[]) => void;
  }
}
interface ConsultationModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ConsultationModal({
  isOpen,
  onClose
}: ConsultationModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent1, setConsent1] = useState(false);
  const [consent2, setConsent2] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Load gtag.js script once on mount
  useEffect(() => {
    if (document.getElementById('gtag-script')) return;
    const script = document.createElement('script');
    script.id = 'gtag-script';
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-830901894';
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', 'AW-830901894');
  }, []);
  // Fire conversion event when thank you screen shows
  useEffect(() => {
    if (submitted && window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-830901894'
      });
    }
  }, [submitted]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent1 || !consent2 || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await fetch('https://services.leadconnectorhq.com/hooks/DBw1tkqBTZflFworLNDq/webhook-trigger/99a04f8d-93f8-4b99-b3b2-11f91e4dcd78', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          phone,
          transactional_consent: consent1,
          marketing_consent: consent2,
          source: 'BBL Landing Page'
        })
      });
    } catch {
      // Still show thank you even if webhook fails
    }
    setIsSubmitting(false);
    setSubmitted(true);
  };
  const handleClose = () => {
    setSubmitted(false);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setConsent1(false);
    setConsent2(false);
    onClose();
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-34">
      <div className="absolute inset-0 bg-brand-text/80 backdrop-blur-sm" onClick={handleClose} data-id="element-35"></div>
      <div className="relative w-full max-w-lg bg-brand-bg rounded-sm shadow-2xl flex flex-col max-h-[90vh] overflow-hidden" data-id="element-36">
        {submitted ? <div className="p-8 md:p-12 text-center" data-id="element-37">
            <button onClick={handleClose} className="absolute top-4 right-4 text-brand-text/40 hover:text-brand-text transition-colors" data-id="element-38">
              <X className="w-6 h-6" data-id="element-39" />
            </button>
            <CheckCircleIcon className="w-16 h-16 text-brand-gold mx-auto mb-6" data-id="element-40" />
            <h3 className="font-playfair text-3xl text-brand-text mb-3" data-id="element-41">
              Thank You!
            </h3>
            <p className="font-sans text-brand-text/70 text-base leading-relaxed mb-8" data-id="element-42">
              Your information has been received. Click below to schedule your
              consultation at a time that works for you.
            </p>
            <Button fullWidth onClick={() => {
          window.location.href = 'https://calendly.com/be_clinical/30min';
        }} data-id="element-43">
              Schedule a Free Consultation
            </Button>
          </div> : <>
            <div className="relative p-6 pb-4 border-b border-brand-divider" data-id="element-44">
              <button onClick={handleClose} className="absolute top-4 right-4 text-brand-text/40 hover:text-brand-text transition-colors" data-id="element-45">
                <X className="w-6 h-6" data-id="element-46" />
              </button>
              <h3 className="font-playfair text-3xl text-brand-text mb-3" data-id="element-47">
                Book Consultation
              </h3>
              <div className="flex items-start gap-2" data-id="element-48">
                <svg className="w-5 h-5 text-brand-text/50 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-49">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" data-id="element-50" />
                  <circle cx="12" cy="10" r="3" data-id="element-51" />
                </svg>
                <div data-id="element-52">
                  <p className="font-sans text-base text-brand-text/70 font-medium" data-id="element-53">
                    Be Clinical Med Spa in San Ramon, CA
                  </p>
                  <p className="font-sans text-sm text-brand-text/50" data-id="element-54">
                    Off of 680 Freeway and Crow Canyon Road
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 overflow-y-auto" data-id="element-55">
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-56">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-57">
                  <div className="space-y-2" data-id="element-58">
                    <label className="font-sans text-sm font-medium text-brand-text" data-id="element-59">
                      First Name
                    </label>
                    <input required type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-3 rounded-sm border border-brand-divider bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/50" data-id="element-60" />
                  </div>
                  <div className="space-y-2" data-id="element-61">
                    <label className="font-sans text-sm font-medium text-brand-text" data-id="element-62">
                      Last Name
                    </label>
                    <input required type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-3 rounded-sm border border-brand-divider bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/50" data-id="element-63" />
                  </div>
                </div>
                <div className="space-y-2" data-id="element-64">
                  <label className="font-sans text-sm font-medium text-brand-text" data-id="element-65">
                    Email
                  </label>
                  <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 rounded-sm border border-brand-divider bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/50" data-id="element-66" />
                </div>
                <div className="space-y-2" data-id="element-67">
                  <label className="font-sans text-sm font-medium text-brand-text" data-id="element-68">
                    Phone Number
                  </label>
                  <input required type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-3 rounded-sm border border-brand-divider bg-white focus:outline-none focus:ring-2 focus:ring-brand-gold/50" data-id="element-69" />
                </div>

                <div className="space-y-4 mt-6 pt-6 border-t border-brand-divider" data-id="element-70">
                  <label className="flex items-start gap-3 cursor-pointer group" data-id="element-71">
                    <div className="flex-shrink-0 mt-1" data-id="element-72">
                      <input type="checkbox" required checked={consent1} onChange={e => setConsent1(e.target.checked)} className="w-4 h-4 rounded-sm border-brand-divider text-brand-gold focus:ring-brand-gold" data-id="element-73" />
                    </div>
                    <span className="font-sans text-xs text-brand-text/70 leading-relaxed" data-id="element-74">
                      By checking this box, I consent to receive non-marketing
                      text messages from Be Clinical about appointment
                      confirmations, service updates, and scheduling. Message
                      frequency varies. Message & data rates may apply. Reply
                      STOP to unsubscribe at any time.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group" data-id="element-75">
                    <div className="flex-shrink-0 mt-1" data-id="element-76">
                      <input type="checkbox" required checked={consent2} onChange={e => setConsent2(e.target.checked)} className="w-4 h-4 rounded-sm border-brand-divider text-brand-gold focus:ring-brand-gold" data-id="element-77" />
                    </div>
                    <span className="font-sans text-xs text-brand-text/70 leading-relaxed" data-id="element-78">
                      By checking this box, I consent to receive marketing text
                      messages from Be Clinical including special offers,
                      discounts, and promotional updates at the phone number
                      provided. Frequency may vary. Message & data rates may
                      apply. Reply STOP to unsubscribe at any time.
                    </span>
                  </label>
                </div>

                <div className="pt-4" data-id="element-79">
                  <Button type="submit" fullWidth disabled={!consent1 || !consent2 || isSubmitting} className="disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-80">
                    {isSubmitting ? 'Submitting...' : 'Continue to Scheduling'}
                  </Button>
                </div>
              </form>
            </div>
          </>}
      </div>
    </div>;
}